import { destructPayment, destructStatus } from '../../destructerers';
import { executeRequest } from '../request';

const executeStatusCheck = async (context, retry = false) => {
  const { config, transactionId: kioskTransactionId } = context; // kioskTransactionId used to validate if the status check is for the same transaction we are sending in the first place

  const status = true;

  logger.info(`[Adyen/Recovery] [TX${kioskTransactionId}] - Initiating transaction status check`);

  try {
    const adyenResponse = await executeRequest({ context, status });

    logger.info(
      `[Adyen/Recovery] [TX${kioskTransactionId}] - Raw status response received: ${JSON.stringify(
        adyenResponse,
        null,
        2,
      )}`,
    );

    // handle destruct
    // return standard format
    const {
      info,
      result,
      response,
      timestamp,
      errorCondition,
      transactionId,
      responseTransactionId,
    } = destructStatus({
      response: adyenResponse,
      config,
      status,
    });

    if (result?.toLowerCase() !== 'success') {
      if (retry) {
        logger.warn(
          `[Adyen/Recovery] [TX${kioskTransactionId}] - Payment not completed after retry`,
        );
        throw new Error('Failed to complete payment');
      }

      // Logging here for visibility, error can get obfuscated via machine
      logger.error(
        `[Adyen/Recovery] [TX${kioskTransactionId}] - Payment failed, no refusal - Reason: ${errorCondition}`,
      );

      throw new Error(errorCondition);
    }

    // this is a failsafe check to verify if the transactionId and amount are the same between request and response.
    // this will help ensure that we're getting the correct transaction status, avoiding receiving old transaction status and validating it as successful
    if (result?.toLowerCase() === 'success') {
      if (responseTransactionId !== kioskTransactionId) {
        logger.error(
          `[Adyen/Recovery] [TX${kioskTransactionId}] - Transaction IDs are different between request and response req: ${kioskTransactionId} and res: ${responseTransactionId}`,
        );

        throw new Error('Transaction IDs are different between request and response');
      }
    }

    return {
      info,
      result,
      message: errorCondition,
      response,
      timestamp,
      transactionId,
      errorCondition,
    };
  } catch (error) {
    throw new Error('Failed to send request');
  }
};

const executeStatusFailureCheck = async context => {
  const { transactionId, message } = context;

  logger.info(`[Adyen/Recovery] [TX${transactionId}] - Checking status request failure...`);

  if (message === 'Failed to send request') {
    logger.error(`[Adyen/Recovery] [TX${transactionId}] - Failed to get transaction status`);
    throw new Error('Failed to get transaction status');
  }

  logger.info(
    `[Adyen/Recovery] [TX${transactionId}] - Transaction status captured, resuming workflow`,
  );

  return { ...context, message: '' };
};

const executePayment = async context => {
  // take built payload
  const { config, transactionId, amount } = context;
  const { retry } = config;

  let adyenResponse;
  const status = false;

  if (retry) {
    logger.info(
      `[Adyen/Recovery] [TX${transactionId}] - Reconnecting to check last known transaction status`,
    );
    const statusCheckResponse = await executeStatusCheck(context, retry);

    if (statusCheckResponse) return statusCheckResponse;
  }

  logger.info(
    `[Adyen/Payment] [TX${transactionId}] - Payment request sent - Amount: ${amount} - Request: ${JSON.stringify(
      context,
      null,
      2,
    )}`,
  );
  try {
    adyenResponse = await executeRequest({ context });

    logger.info(
      `[Adyen/Payment] [TX${transactionId}] - Raw payment response received: ${JSON.stringify(
        adyenResponse,
        null,
        2,
      )}`,
    );

    // handle destruct
    // return standard format
    const { result, errorCondition, response, transactionId, timestamp, info } = destructPayment({
      response: adyenResponse,
      config,
      status,
    });

    if (result?.toLowerCase() !== 'success') {
      throw new Error('Payment failed.');
    }

    return {
      result,
      message: errorCondition,
      errorCondition,
      response,
      transactionId,
      timestamp,
      info,
    };
  } catch (error) {
    logger.error(`[Adyen/Payment] [${context.transactionId}] - Payment request failed`, error);
    return executeStatusCheck({ context, retry: false });
  }
};

export { executePayment, executeStatusCheck, executeStatusFailureCheck };
